import styles from './EnergyTypeOverview.module.scss'
import { Heading, Tag } from '@boltenergy-be/design-system'
import { useTranslation } from 'react-i18next'
import { ENERGY_TYPES } from '@boltenergy-be/shared'
import dayjs from 'dayjs'
import { DATE_FORMAT } from 'constants/constants.ts'
import Link from 'components/link/Link.tsx'
import { EnergyTypeOverviewProps } from './types.ts'
import { formatContractStatus } from 'utils/contracts.ts'
import { formatAddress } from 'utils/user.ts'
import parse from 'html-react-parser'
import { AddressFormats } from 'types/format.ts'

const EnergyTypeOverview = ({ serviceContract, priceLists, contractualStartDate }: EnergyTypeOverviewProps) => {
  // i18n
  const { t } = useTranslation(['tariffs', 'common', 'addresses'])

  // Constants
  const contractStatus = formatContractStatus(serviceContract)
  const formattedAddress = formatAddress(serviceContract?.deliveryPoint?.address, AddressFormats.FULL_TWO_LINES)
  const mostFuturePriceList = priceLists?.future?.sort((a, b) => dayjs(a.fromDate).diff(dayjs(b.fromDate)))?.[0]
  const relevantPriceList = priceLists?.current || mostFuturePriceList || null

  return (
    <section>
      <header className={styles.header}>
        <Heading as="h3" variant="h5" className={styles.title}>
          {t(($) => $[serviceContract.detail.energyType === ENERGY_TYPES.gas ? 'gas' : 'electricity'], { ns: 'common' })}
        </Heading>
        <Tag type={contractStatus.type} className={styles.tag} size="small">
          {t(($) => $.overview.statuses[contractStatus.key])}
        </Tag>
      </header>

      {/* ADDRESS */}
      {formattedAddress && (
        <div className={styles.item}>
          <strong>
            {t(($) => $.myContract.address, {
              ns: 'addresses'
            })}
          </strong>
          <span>{parse(formattedAddress)}</span>
        </div>
      )}

      {/* EAN */}
      <div className={styles.item}>
        <strong>
          {t(($) => $.myContract.ean, {
            ns: 'addresses'
          })}
        </strong>
        <span>{serviceContract.deliveryPoint.ean}</span>
      </div>

      {/* METER NUMBER */}
      {!!serviceContract.deliveryPoint.meterNumber && (
        <div className={styles.item}>
          <strong>
            {t(($) => $.myContract.meterNumber, {
              ns: 'addresses'
            })}
          </strong>
          <span>{serviceContract.deliveryPoint.meterNumber}</span>
        </div>
      )}

      {!!serviceContract.deliveryPoint.gridOperatorName && (
        <div className={styles.item}>
          <strong>
            {t(($) => $.myContract.dgo, {
              ns: 'addresses'
            })}
          </strong>
          <span>{serviceContract.deliveryPoint.gridOperatorName}</span>
        </div>
      )}

      <div className={styles.item}>
        {relevantPriceList?.fromDate && (
          <div className={styles['sub-item']}>
            <strong>{t(($) => $.overview.currentTariff.info.startDate)}</strong>
            <span>{dayjs(relevantPriceList.fromDate).format(DATE_FORMAT)}</span>
          </div>
        )}

        {contractualStartDate && (
          <div className={styles['sub-item']}>
            <strong>{t(($) => $.overview.clientSince)}</strong>
            <span>{dayjs(contractualStartDate).format(DATE_FORMAT)}</span>
          </div>
        )}
      </div>

      {relevantPriceList?.url && (
        <div className={styles.item}>
          <strong>{t(($) => $.overview.currentTariff.info.currentTariffCard)}</strong>
          <Link
            as="a"
            download
            href={!relevantPriceList.url.startsWith('https://') ? `https://${relevantPriceList.url}` : relevantPriceList.url}
            className="text-sm weight-semibold"
            target="_blank"
            trailingIcon="download"
          >
            {t(($) => $.tariffCard, {
              ns: 'common'
            })}
          </Link>
        </div>
      )}
    </section>
  )
}

export default EnergyTypeOverview
